import { CSSProperties, ReactNode, useEffect, useState } from "react";
import "./WalletPassLoader.css";

export type WalletPassLoaderStatus = "loading" | "success";
export type WalletPassLoaderSize = "sm" | "md";

export interface WalletPassLoaderStage {
  label: string;
  icon?: ReactNode;
}

export interface WalletPassLoaderProps {
  status?: WalletPassLoaderStatus;
  size?: WalletPassLoaderSize;
  stages?: WalletPassLoaderStage[];
  stageDuration?: number;
  showLabel?: boolean;
  color?: string;
  background?: string;
  className?: string;
  style?: CSSProperties;
}

const PassIcon = () => (
  <svg viewBox="0 0 24 24">
    <rect x="4" y="3" width="16" height="18" rx="3" />
    <path d="M8 8 h8" />
    <path d="M8 12 h6" />
    <path d="M8 16 h4" />
  </svg>
);

const ShieldIcon = () => (
  <svg className="wp-icon-shield" viewBox="0 0 24 24">
    <path d="M12 3 l8 3 v6 c0 5 -3.5 8 -8 9 c-4.5 -1 -8 -4 -8 -9 V6 z" />
    <path className="shield-check" d="M9 12 l2.5 2.5 L16 10" />
  </svg>
);

const AddToWalletIcon = () => (
  <svg className="wp-icon-add" viewBox="0 0 24 24">
    <rect x="3" y="9" width="18" height="11" rx="2" />
    <path d="M7 9 V7 a5 5 0 0 1 10 0 v2" />
    <g className="arrow-down">
      <path d="M12 12 v4" />
      <path d="M9.5 14.5 L12 17 l2.5 -2.5" />
    </g>
  </svg>
);

const DEFAULT_STAGES: WalletPassLoaderStage[] = [
  { label: "Creating pass", icon: <PassIcon /> },
  { label: "Signing pass", icon: <ShieldIcon /> },
  { label: "Adding to wallet", icon: <AddToWalletIcon /> },
];

export function WalletPassLoader({
  status = "loading",
  size = "md",
  stages = DEFAULT_STAGES,
  stageDuration = 2,
  showLabel = true,
  color,
  background,
  className,
  style,
}: WalletPassLoaderProps) {
  const [active, setActive] = useState(0);
  const [prev, setPrev] = useState(-1);

  useEffect(() => {
    if (status !== "loading" || stages.length <= 1) return;
    const id = window.setInterval(() => {
      setActive((curr) => {
        setPrev(curr);
        return (curr + 1) % stages.length;
      });
    }, Math.max(0.5, stageDuration) * 1000);
    return () => window.clearInterval(id);
  }, [status, stages.length, stageDuration]);

  useEffect(() => {
    if (status !== "loading") {
      setActive(stages.length - 1);
      setPrev(-1);
    }
  }, [status, stages.length]);

  const cssVars: CSSProperties = {
    ...(color ? ({ ["--wp-color" as never]: color } as CSSProperties) : {}),
    ...(background ? ({ ["--wp-bg" as never]: background } as CSSProperties) : {}),
    ...style,
  };

  const stageClass = (i: number) => {
    if (i === active) return "wp-stage-item is-active";
    if (i === prev) return "wp-stage-item is-prev";
    return "wp-stage-item";
  };

  const rootClass = ["wp-root", `wp-${size}`, className].filter(Boolean).join(" ");
  const stageBoxClass = ["wp-stage", status === "success" && "is-success"].filter(Boolean).join(" ");

  return (
    <div className={rootClass} style={cssVars} role="status" aria-live="polite">
      <div className={stageBoxClass}>
        <div className="wp-wallet" />

        <div className="wp-pass">
          <div className="wp-header">
            <div className="wp-logo" />
            <div className="wp-brand" />
          </div>

          <div className="wp-body">
            <div className="wp-row">
              <div className="wp-label" />
              <div className="wp-value" />
            </div>
            <div className="wp-row">
              <div className="wp-label" />
              <div className="wp-value" />
            </div>
          </div>

          <div className="wp-barcode">
            <div className="wp-bars">
              {Array.from({ length: 20 }).map((_, i) => <span key={i} />)}
            </div>
            <div className="wp-barcode-id" />
          </div>

          <div className="wp-beam" />
        </div>
      </div>

      {showLabel && status === "loading" && stages.length > 0 && (
        <div className="wp-stages">
          <div className="wp-stage-icon-wrap">
            {stages.map((stage, i) => (
              <div key={i} className={stageClass(i)}>{stage.icon}</div>
            ))}
          </div>
          <div className="wp-stage-text-wrap">
            {stages.map((stage, i) => (
              <div key={i} className={stageClass(i)}>
                <span className="wp-stage-text">
                  {stage.label}
                  <span className="wp-dots" />
                </span>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

export default WalletPassLoader;
